import { Mesh, type Object3D } from 'three'
import { ThreeOriginalFileTypes } from '../../../constants/three'
import { OBJLoader } from 'three/examples/jsm/Addons.js'
import { setFileLoadingStatus } from '../../../stores/file-loading'
import MeshFileLoader from './mesh-file-loader'
import { toIndexedGeometry } from '../../../helpers/three/mesh-indexing'

class OBJFileLoader extends MeshFileLoader {
  protected getWorkerFileType(): FileParserSupportedType {
    return 'obj'
  }

  protected getOriginalFileType(): typeof ThreeOriginalFileTypes[keyof typeof ThreeOriginalFileTypes] {
    return ThreeOriginalFileTypes.OBJ
  }

  protected processParsedObjects(parsedObjects: Object3D[]): Object3D[] {
    const meshes: Object3D[] = []

    for (const obj of parsedObjects) {
      obj.traverse((child) => {
        if (child instanceof Mesh) {
          const scaled = this.scaleDownObject(child, 0.01) as Mesh
          scaled.material = this.getStandardMaterial()
          meshes.push(scaled)
        }
      })
    }

    return meshes
  }

  protected processOnMainThread(): Object3D[] {
    setFileLoadingStatus('parsing', this.file.name, 30)

    const loader = new OBJLoader()
    const text = new TextDecoder().decode(this.arrayBuffer!)

    this.arrayBuffer = undefined

    setFileLoadingStatus('processing', this.file.name, 50)

    const group = loader.parse(text)

    setFileLoadingStatus('finalizing', this.file.name, 80)

    // Geometry is no longer simplified anywhere — meshes are kept at full resolution.
    return group.children.map((child) => {
      // Index the (non-indexed) OBJ geometry up front, as early as STL does.
      if (child instanceof Mesh) {
        child.geometry = toIndexedGeometry(child.geometry)
      }
      const newChild = this.scaleDownObject(child, 0.01) as Mesh
      newChild.material = this.getStandardMaterial()
      return newChild
    })
  }
}

export default OBJFileLoader
